'use client';
import React, { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Users } from 'lucide-react';

interface Props {
  city: string;
  locationId?: string;
  className?: string;
}

export default function RealtimePlayerCount({ city, locationId, className = '' }: Props) {
  const [count, setCount] = useState<number | null>(null);
  const supabase = createClient();

  const fetchCount = async () => {
    try {
      let query = supabase
        .from('player_presence')
        .select('*', { count: 'exact', head: true })
        .eq('is_online', true);

      if (locationId) {
        query = query.eq('location_id', locationId);
      } else {
        query = query.eq('city', city);
      }

      const { count: c } = await query;
      setCount(c ?? 0);
    } catch {
      setCount(null);
    }
  };

  useEffect(() => {
    fetchCount();

    const channel = supabase
      .channel(`realtime:count:${locationId || city}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'player_presence',
        ...(locationId ? { filter: `location_id=eq.${locationId}` } : { filter: `city=eq.${city}` }),
      }, () => {
        fetchCount();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [city, locationId]);

  if (count === null) return null;

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <div className="w-2 h-2 rounded-full bg-game-success animate-pulse" />
      <Users size={12} className="text-game-success" />
      <span className="text-xs font-mono text-game-success tabular-nums">{count} онлайн</span>
    </div>
  );
}
